import type {
  FileEntryCondition,
  FormColumn,
  FormRow,
  FormSection,
  SourceFieldRef,
  WorkflowField,
} from '@/api/workflows';

/**
 * Front-end mirror of the back-end's canonical `ConditionStripper` (byte-for-byte
 * algorithm match — see apps/back-end/.../Domain/CustomElements/ConditionStripper.cs).
 * Runs client-side purely for immediate UX (stripping before the save-as-custom POST);
 * the BE re-runs the same logic server-side as defense-in-depth and never trusts this
 * output. All functions are pure — they return new objects, never mutate the input.
 *
 * Rules:
 *   1. An element's own top-level gating condition is always dropped.
 *   2. A nested condition list (field-inside-section, section/field-inside-step)
 *      survives only if EVERY condition's fieldId resolves to a field, OR a
 *      repeatable section's own id (`count_eq`/`count_ge` — see
 *      `evaluateConditions.ts`), also inside the same saved subtree —
 *      otherwise the WHOLE list is cleared, never partially.
 *   3. At step scope, a surviving condition must ALSO match fieldStepId against the
 *      step being saved (a FormSection carries no step id, so section scope only
 *      checks fieldId membership).
 *   4. A field's `sourceField` (cross-step prefill reference) follows the same
 *      membership rule as a condition: it survives only if the referenced field is
 *      also inside the saved subtree (and, at step scope, points at the step being
 *      saved); otherwise it's cleared. Evaluated INDEPENDENTLY of the field's
 *      conditions — one can survive while the other is cleared.
 *   5. A Date field's `mustFollowFieldId` (date-ordering reference) survives only
 *      if the referenced field is inside the saved subtree.
 *   6. The "Disable if" list (`disableConditions`) follows rules 1-3 in full, and is
 *      evaluated INDEPENDENTLY of the visibility list — one can survive while the other
 *      is cleared, exactly as rule 4's `sourceField` does.
 */

function allReferencesWithin(
  conditions: FileEntryCondition[],
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): boolean {
  if (conditions.length === 0) return true;
  return conditions.every((c) => {
    if (!fieldIds.has(c.fieldId)) return false;
    if (sourceStepId !== undefined && c.fieldStepId !== sourceStepId) return false;
    return true;
  });
}

/** True when the ref is absent (nothing to strip) or its target field lives inside
 *  the saved subtree — and, at step scope, points at the step being saved. Mirrors
 *  {@link allReferencesWithin} for a single reference. A conditional prefill survives
 *  only with its whole gate inside the subtree too. */
function sourceFieldWithin(
  ref: SourceFieldRef | null | undefined,
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): boolean {
  if (!ref) return true;
  if (!fieldIds.has(ref.fieldId)) return false;
  if (sourceStepId !== undefined && ref.stepId !== sourceStepId) return false;
  return allReferencesWithin(ref.conditions ?? [], fieldIds, sourceStepId);
}

function clearedFieldConditions(field: WorkflowField): WorkflowField {
  return { ...field, conditions: [], conditionCombinator: null };
}

function clearedFieldDisableConditions(field: WorkflowField): WorkflowField {
  return { ...field, disableConditions: [], disableConditionCombinator: null };
}

/** Clears a field's cross-references (conditions + sourceField) independently: each
 *  survives only if it resolves inside the saved subtree. */
function sanitizeFieldReferences(
  field: WorkflowField,
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): WorkflowField {
  let result = field;
  if (!allReferencesWithin(field.conditions, fieldIds, sourceStepId)) {
    result = clearedFieldConditions(result);
  }
  // Evaluated INDEPENDENTLY of the visibility list above — one can survive while the
  // other is cleared, exactly like `sourceField` below.
  if (!allReferencesWithin(field.disableConditions ?? [], fieldIds, sourceStepId)) {
    result = clearedFieldDisableConditions(result);
  }
  if (!sourceFieldWithin(field.sourceField, fieldIds, sourceStepId)) {
    result = { ...result, sourceField: null };
  }
  if (field.mustFollowFieldId && !fieldIds.has(field.mustFollowFieldId)) {
    result = { ...result, mustFollowFieldId: null };
  }
  return result;
}

function clearedSection(section: FormSection): FormSection {
  return { ...section, conditions: [], conditionCombinator: null };
}

function clearedSectionDisableConditions(section: FormSection): FormSection {
  return { ...section, disableConditions: [], disableConditionCombinator: null };
}

/**
 * Every id a condition/sourceField inside `sections` may legitimately point
 * at: every field's id, PLUS every REPEATABLE section's own id (a
 * `count_eq`/`count_ge` condition's `fieldId` is a sectionId, not a field id
 * — see `evaluateConditions.ts`). Without the section ids, a step-scope count
 * condition referencing an earlier section in the SAME saved step would look
 * like a dangling reference and get its whole condition list wiped, even
 * though the section it points at is right there in the saved subtree.
 */
function collectFieldIds(sections: FormSection[]): Set<string> {
  const ids = new Set<string>();
  for (const section of sections) {
    if (section.repeatable) ids.add(section.id);
    for (const row of section.rows) {
      for (const column of row.columns) {
        if (column.field) ids.add(column.field.id);
      }
    }
  }
  return ids;
}

function stripFieldsInRows(
  rows: FormRow[],
  fieldIds: ReadonlySet<string>,
  sourceStepId?: string,
): FormRow[] {
  return rows.map((row) => ({
    ...row,
    columns: row.columns.map((column): FormColumn => {
      if (!column.field) return column;
      return { ...column, field: sanitizeFieldReferences(column.field, fieldIds, sourceStepId) };
    }),
  }));
}

/** Strips a field's own cross-references. A standalone field has no subtree, so any
 *  visibility condition OR sourceField reference points outside the saved element
 *  and is always cleared. */
export function stripFieldForCustomElement(field: WorkflowField): WorkflowField {
  return {
    ...clearedFieldDisableConditions(clearedFieldConditions(field)),
    sourceField: null,
    mustFollowFieldId: null,
  };
}

/**
 * Strips a section's own visibility condition, plus clears any nested field
 * condition that references a field outside the section (whole list, not partial).
 */
export function stripSectionForCustomElement(section: FormSection): FormSection {
  const fieldIds = collectFieldIds([section]);
  const rows = stripFieldsInRows(section.rows, fieldIds);
  return clearedSectionDisableConditions(clearedSection({ ...section, rows }));
}

/**
 * Strips conditions one level up: every section/field inside the given step's
 * sections keeps its condition only if it references a field inside the SAME
 * step AND (for sections) the condition's fieldStepId matches sourceStepId.
 * A step itself carries no top-level visibility condition to clear (mirrors
 * CustomStepContent having no Conditions property on the back end).
 */
export function stripStepForCustomElement(
  sections: FormSection[],
  sourceStepId: string,
): FormSection[] {
  const fieldIds = collectFieldIds(sections);
  return sections.map((section) => {
    const withClearedOwnCondition = allReferencesWithin(section.conditions, fieldIds, sourceStepId)
      ? section
      : clearedSection(section);
    // Independent of the visibility list, same as a field's — see sanitizeFieldReferences.
    const withClearedDisable = allReferencesWithin(
      section.disableConditions ?? [],
      fieldIds,
      sourceStepId,
    )
      ? withClearedOwnCondition
      : clearedSectionDisableConditions(withClearedOwnCondition);
    return {
      ...withClearedDisable,
      rows: stripFieldsInRows(withClearedDisable.rows, fieldIds, sourceStepId),
    };
  });
}
